import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import useAuth from "./auth.context.jsx";
import groupServices from "../src/services/groupServices";

const ACTIVE_GROUP_KEY = "activeGroupId";

export const GroupContext = createContext();
GroupContext.displayName = "Group";

export function GroupProvider({ children }) {
  const { user, userData } = useAuth();
  const [myGroups, setMyGroups] = useState([]);
  const [archivedGroups, setArchivedGroups] = useState([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState(localStorage.getItem(ACTIVE_GROUP_KEY));
  // Tagged with the group it belongs to. Without the tag the previous group's
  // members stay in context during the switch, and anything reading
  // activeGroupDetails — the split editor, the settlement form, the report's
  // member average — silently uses the wrong roster until the fetch lands.
  const [detailsState, setDetailsState] = useState({ groupId: null, data: null });

  // Both lists are fetched together (not lazily) so that an activeGroupId left
  // over from before a group was archived — e.g. the manager who just archived
  // it, or a reload with that id still in localStorage — resolves to its
  // archived record instead of silently falling back to a different group.
  const refreshGroups = useCallback(async () => {
    if (!user) {
      setMyGroups([]);
      setArchivedGroups([]);
      setGroupsLoaded(false);
      return;
    }
    try {
      const [{ groups }, archivedRes] = await Promise.all([
        groupServices.getMyGroups(),
        groupServices.getArchivedGroups().catch(() => ({ groups: [] })),
      ]);
      setMyGroups(groups || []);
      setArchivedGroups(archivedRes.groups || []);
    } catch {
      setMyGroups([]);
      setArchivedGroups([]);
    } finally {
      setGroupsLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    refreshGroups();
  }, [refreshGroups]);

  const activeGroup =
    myGroups.find((g) => g._id === activeGroupId) ||
    archivedGroups.find((g) => g._id === activeGroupId) ||
    myGroups[0] ||
    null;

  // A finished trip is read-only: the picker, the group screen, and every
  // write button across the app key off this single flag. Sourced from
  // activeGroup (myGroups/archivedGroups, refreshed by refreshGroups) rather
  // than activeGroupDetails — the latter is fetched separately and would lag
  // one render behind right after an archive/unarchive call.
  const isGroupArchived = !!activeGroup?.archivedAt;
  const lockedIds = userData?.entitlements?.lockedGroupIds || [];
  const isGroupWriteLocked = !!activeGroup && lockedIds.some((id) => String(id) === String(activeGroup._id));

  const setActiveGroup = (groupId) => {
    localStorage.setItem(ACTIVE_GROUP_KEY, groupId);
    setActiveGroupId(groupId);
  };

  // Which group the app is showing right now, readable from inside a resolved
  // promise. Switching A -> B -> A fires three fetches; if B's lands last it
  // would tag the state with B while the app shows A, and since no effect is
  // pending for A the roster would stay empty until a reload. Late responses
  // for a group that is no longer active are dropped instead.
  const activeIdRef = useRef(null);
  activeIdRef.current = activeGroup?._id || null;

  // load full group details (members with names/phones) for the active group
  const refreshGroupDetails = useCallback(async () => {
    if (!activeGroup) {
      setDetailsState({ groupId: null, data: null });
      return;
    }
    const groupId = activeGroup._id;
    try {
      const { group } = await groupServices.getGroup(groupId);
      if (activeIdRef.current === groupId) setDetailsState({ groupId, data: group });
    } catch {
      if (activeIdRef.current === groupId) setDetailsState({ groupId, data: null });
    }
  }, [activeGroup?._id]);

  useEffect(() => {
    refreshGroupDetails();
  }, [refreshGroupDetails]);

  // Never hand out a roster that belongs to a different group than the one the
  // rest of the app is showing.
  const activeGroupDetails = activeGroup && detailsState.groupId === activeGroup._id
    ? detailsState.data
    : null;

  return (
    <GroupContext.Provider
      value={{
        myGroups,
        archivedGroups,
        groupsLoaded,
        activeGroup,
        activeGroupDetails,
        isGroupArchived,
        isGroupWriteLocked,
        setActiveGroup,
        refreshGroups,
        refreshGroupDetails,
      }}
    >
      {children}
    </GroupContext.Provider>
  );
}

export default function useGroup() {
  return useContext(GroupContext);
}
